import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { MockBadge, Button, Spinner } from "../components/ui";
import { Send, Link2, Copy, CheckCircle2, AlertCircle, Unlink2 } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

export default function Settings() {
  const { t } = useI18n();
  const [tg, setTg] = useState(null);
  const [linkInfo, setLinkInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/integrations/telegram/status");
    setTg(data);
  };

  useEffect(() => { refresh(); }, []);

  const startLink = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/integrations/telegram/link-token");
      setLinkInfo(data);
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    if (!linkInfo) return;
    navigator.clipboard.writeText(linkInfo.deep_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const sendTest = async () => {
    setBusy(true);
    setMsg("");
    try {
      await api.post("/integrations/telegram/test");
      setMsg(t("settings.testMessageSent"));
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    setBusy(true);
    try {
      await api.delete("/integrations/telegram/unlink");
      setLinkInfo(null);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  if (!tg) return <div className="p-10"><Spinner /></div>;

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">{t("settings.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("settings.title")}</h1>
          <p className="mt-2 text-xs text-text-muted">{t("settings.subtitle")}</p>
        </div>
        <MockBadge />
      </div>

      <div className="grid grid-cols-12">
        {/* Telegram card */}
        <section className="col-span-12 lg:col-span-7 border-r border-border p-6">
          <div className="overline">{t("settings.channelTelegram")}</div>
          <h3 className="mt-1 font-heading text-2xl font-bold">{t("settings.telegramAlerts")}</h3>
          <p className="mt-2 text-xs text-text-muted max-w-lg">
            {t("settings.telegramDesc")}
          </p>

          <div className="mt-6 border border-border bg-surface p-5">
            <div className="flex items-center gap-3">
              {tg.bot_configured ? <CheckCircle2 size={18} className="text-signal-green" /> : <AlertCircle size={18} className="text-signal-yellow" />}
              <div className="flex-1">
                <div className="text-sm">
                  {t("settings.bot")} <span className="font-mono text-white">@{tg.bot_username}</span>
                </div>
                <div className="text-[10px] font-mono text-text-muted mt-0.5">
                  {tg.bot_configured ? t("settings.botConfigured") : t("settings.botNotConfigured")}
                </div>
              </div>
              <div data-testid="tg-status-chip" className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest2 border ${
                tg.linked
                  ? "border-signal-green text-signal-green bg-signal-green/10"
                  : "border-border text-text-muted"
              }`}>
                {tg.linked ? t("settings.linked") : t("settings.notLinked")}
              </div>
            </div>
          </div>

          {!tg.linked && (
            <div className="mt-5 border border-border p-5">
              <div className="overline">{t("settings.step1")}</div>
              <Button
                onClick={startLink}
                variant="green"
                disabled={busy || !tg.bot_configured}
                data-testid="tg-link-btn"
                className="mt-3"
              >
                {busy ? <Spinner /> : <Link2 size={14} />} {t("settings.connectTelegram")}
              </Button>

              {linkInfo && (
                <div className="mt-5 reveal">
                  <div className="overline">{t("settings.step2")}</div>
                  <div className="mt-2 flex items-center gap-2 border border-border bg-bg p-3">
                    <code className="flex-1 font-mono text-xs text-signal-green truncate">{linkInfo.deep_link}</code>
                    <button
                      onClick={copy}
                      data-testid="tg-copy-link"
                      className="text-text-muted hover:text-white p-1"
                      title={t("settings.copy")}
                    >
                      {copied ? <CheckCircle2 size={14} className="text-signal-green" /> : <Copy size={14} />}
                    </button>
                  </div>
                  <a
                    href={linkInfo.deep_link}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-[10px] font-mono uppercase tracking-widest2 text-signal-green hover:underline"
                  >
                    {t("settings.openInTelegram")}
                  </a>
                  <div className="mt-3 text-[10px] font-mono text-text-muted">
                    {t("settings.tapStartNote")}
                  </div>
                  <Button onClick={refresh} variant="ghost" className="mt-3">
                    {t("settings.verifyLink")}
                  </Button>
                </div>
              )}
            </div>
          )}

          {tg.linked && (
            <div className="mt-5 border border-signal-green/30 bg-signal-green/5 p-5">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={20} className="text-signal-green" />
                <div className="flex-1">
                  <div className="text-sm">{t("settings.linkedToChatId", { id: tg.chat_id })}</div>
                  <div className="text-[10px] font-mono text-text-muted mt-0.5">
                    {t("settings.alertsDeliveredHere")}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <Button onClick={sendTest} variant="green" disabled={busy} data-testid="tg-test-btn">
                  {busy ? <Spinner /> : <Send size={14} />} {t("settings.sendTestMessage")}
                </Button>
                <Button onClick={unlink} variant="danger" disabled={busy} data-testid="tg-unlink-btn">
                  <Unlink2 size={14} /> {t("settings.unlink")}
                </Button>
              </div>
            </div>
          )}

          {msg && (
            <div className="mt-4 text-xs font-mono text-text-secondary border border-border p-3">
              {msg}
            </div>
          )}
        </section>

        {/* Right column — info */}
        <aside className="col-span-12 lg:col-span-5 p-6 space-y-4">
          <div className="border border-border p-4">
            <div className="overline">{t("settings.comingSoon")}</div>
            <ul className="mt-2 text-xs text-text-secondary font-mono space-y-1">
              <li>{t("settings.comingSoon1")}</li>
              <li>{t("settings.comingSoon2")}</li>
              <li>{t("settings.comingSoon3")}</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
